/**
 * Centralized, read-only application configuration.
 *
 * All values originate from Vite environment variables (`VITE_*`), which are
 * inlined at build time and are therefore public. Never place client secrets
 * here — this application is a public client (SPA) and authenticates users
 * interactively via MSAL.
 */

const PLACEHOLDER = '__CONFIGURE_IN_DEPLOYMENT__'

export interface AppConfig {
  copilotDirectConnectUrl: string
  entraClientId: string
  entraTenantId: string
  entraRedirectUri: string
  /** Dedicated blank redirect target for popup/silent auth flows (see public/auth/end.html). */
  entraPopupRedirectUri: string
  /** Scope used to call the app's own conversations API (api://<clientId>/access_as_user). */
  apiScope: string
  /** Base URL for the conversations API. Empty = same origin (production). */
  apiBaseUrl: string
  environmentLabel: string
}

const rawEnv = import.meta.env

function isConfigured(value: string | undefined): value is string {
  return Boolean(value) && value !== PLACEHOLDER
}

const entraClientId = rawEnv.VITE_ENTRA_CLIENT_ID ?? ''

export const appConfig: AppConfig = {
  copilotDirectConnectUrl: rawEnv.VITE_COPILOT_DIRECT_CONNECT_URL ?? '',
  entraClientId,
  entraTenantId: rawEnv.VITE_ENTRA_TENANT_ID ?? '',
  entraRedirectUri: rawEnv.VITE_ENTRA_REDIRECT_URI || window.location.origin,
  entraPopupRedirectUri: `${window.location.origin}/auth/end.html`,
  apiScope: rawEnv.VITE_API_SCOPE || (entraClientId ? `api://${entraClientId}/access_as_user` : ''),
  apiBaseUrl: rawEnv.VITE_API_BASE_URL ?? '',
  environmentLabel: rawEnv.VITE_APP_ENVIRONMENT_LABEL || 'Development',
}

export interface ConfigIssue {
  key: string
  message: string
}

/**
 * Validates the configuration required to authenticate and connect to the
 * SOC-Copilot agent. Used to render a clear, actionable setup error instead
 * of a blank page or a silent failure.
 */
export function getConfigIssues(): ConfigIssue[] {
  const issues: ConfigIssue[] = []

  if (!isConfigured(appConfig.entraClientId)) {
    issues.push({
      key: 'VITE_ENTRA_CLIENT_ID',
      message:
        'Microsoft Entra application (client) ID is not configured. Set VITE_ENTRA_CLIENT_ID in your .env file.',
    })
  }

  if (!isConfigured(appConfig.entraTenantId)) {
    issues.push({
      key: 'VITE_ENTRA_TENANT_ID',
      message:
        'Microsoft Entra tenant ID is not configured. Set VITE_ENTRA_TENANT_ID in your .env file.',
    })
  }

  if (!isConfigured(appConfig.copilotDirectConnectUrl)) {
    issues.push({
      key: 'VITE_COPILOT_DIRECT_CONNECT_URL',
      message:
        'The Copilot Studio direct connection URL is not configured. Set VITE_COPILOT_DIRECT_CONNECT_URL in your .env file.',
    })
  }

  return issues
}

export function isAppConfigured(): boolean {
  return getConfigIssues().length === 0
}
